import { cn } from "@lib/utils/cn";
import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** Allow ESC / backdrop click to close. Default true. */
  dismissable?: boolean;
  className?: string;
  children: ReactNode;
  footer?: ReactNode;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Premium shared modal using native <dialog> + showModal().
 * Renders in the browser top-layer — immune to ancestor overflow/transform clipping.
 * Handles: focus trap, ESC close, backdrop click, body scroll lock, exit animations.
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  size = "md",
  dismissable = true,
  className,
  children,
  footer,
  initialFocusRef,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);
  const titleId = useId();
  const triggerRef = useRef<Element | null>(null);

  // Control native dialog open/close (always mounted, effect-driven)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      triggerRef.current = document.activeElement;
      dialog.showModal();
      // Focus initial element if provided, else first focusable
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      }
    } else if (!open && dialog.open) {
      setClosing(true);
      dialog.close();
      // Restore focus to trigger element
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    }
  }, [open, initialFocusRef]);

  // Reset closing state after exit animation completes
  const handleAnimationEnd = () => {
    setClosing(false);
  };

  // ESC key — prevent close if not dismissable
  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    if (!dismissable) {
      e.preventDefault();
      return;
    }
    onClose();
  };

  // Backdrop click — the dialog element itself is the backdrop in DaisyUI
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current && dismissable) {
      onClose();
    }
  };

  // Prevent form submissions from closing the dialog (unless method="dialog")
  const handleFormSubmit = (e: React.FormEvent<HTMLDialogElement>) => {
    const target = e.target as HTMLElement;
    const form = target.closest("form");
    if (form && form.getAttribute("method") !== "dialog") {
      e.preventDefault();
    }
  };

  const hasHeader = title || icon;

  return (
    <dialog
      ref={dialogRef}
      className={cn("dialog", `dialog-size-${size}`, closing && "closing", className)}
      onClose={onClose}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      onSubmit={handleFormSubmit}
      onAnimationEnd={handleAnimationEnd}
      aria-labelledby={hasHeader ? titleId : undefined}
    >
      <div className="dialog-panel">
        {hasHeader && (
          <div className="dialog-header">
            <div className="flex items-start gap-3">
              {icon && <div className="dialog-icon">{icon}</div>}
              <div>
                {title && (
                  <h3 id={titleId} className="dialog-title">
                    {title}
                  </h3>
                )}
                {subtitle && <p className="dialog-subtitle">{subtitle}</p>}
              </div>
            </div>
            <button type="button" className="dialog-close" onClick={onClose} aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="dialog-body">{children}</div>

        {footer && <div className="dialog-footer">{footer}</div>}
      </div>
    </dialog>
  );
}
